import {
	AdyenEnvironments,
	getAdyenClientData,
	loadAdyenDataCollectionScript,
} from '@shipstation/financial-services-ui-sdk';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { isString } from 'lodash';
import React, { Component } from 'react';
import ReactDOMServer from 'react-dom/server';
import Chatbot from '../chatbot';
import ModalModule from '../modal';
import UI from '../ui';
import Link from '../ui/Link.component';
import Utils from '../utils';
import Communication from '../utils/Communication';
import AccountBalanceLimitModal from './AccountBalanceLimitModal.component';
import AutoFundAccountModal from './AutoFundAccountModal.component';
import ConfirmTransactionModal from './ConfirmTransactionModal.component';
import { FundingLimitReachedModal } from './FundingLimitReachedModal.component';
import { FundingRestrictedModal } from './FundingRestrictedModal.component';
import { withIdentityVerification } from './hoc';
import ProcessingPurchaseModal from './ProcessingPurchaseModal.component';
import { TotalChargeSummary } from './TotalChargeSummary.component';
import { TransactionCompleteModal } from './TransactionCompleteModal.component';

const { Modal, ModalButtonActionType } = ModalModule;
const {
	withLayoutAndModalContexts,
	Navigation,
	Session,
	Analytics,
	Logger,
	BillingPlans,
} = Utils;
const {
	WrappedButton,
	WrappedButtonType,
	WrappedRadioButton,
	WrappedInputNumberWithLabel,
	InlineMessage,
	Spinner,
} = UI;

const { ChatbotTriggers, launchChatbot } = Chatbot;

// Use JavaScript Intl.NumberFormat to format fund amount value
const usCurrencyFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
});

const ACCOUNT_STATUS_RESTRICTED = 'Restricted';

const getAdyenEnvironment = () =>
	Session.getSettings().IsTestEnvironment
		? AdyenEnvironments.Test
		: AdyenEnvironments.Live;

class AddFundsModal extends Component {
	constructor(props) {
		super(props);

		this.state = {
			customAmount: null,
			selectedOption: '',
			purchaseAmount: null,
			showInlineErrorMessage: false,
			isNsaSurchargeEnrolled: false,
			nsaSurchargeRate: 0,
			previewCalculations: {
				totalAmount: 0,
				purchaseAmount: 0,
				surchargeAmount: 0,
			},
			isLoadingPreview: false,
			isInitialFundsPurchase: false,
			isFundingLimitReached: false,
			maxFundingLimit: null,
			isCheckingFundingLimit: false,
		};

		this.customAmountDebounceTimer = null;
	}

	componentDidMount() {
		const { isNsaSurchargeEnrolled, nsaSurchargePercentage } =
			Session.getDynamicSettings();
		const { controlTotal = 0 } = Session.getBalance(); // controlTotal is the total funds added to the account
		const maxFundingLimit = Session.getMaxFundingLimit().maxFundingLimit;

		const isNewLineupPlan = BillingPlans.isNewLineupPlan();
		const hasCachedLimit = maxFundingLimit !== null;
		const isAccountRestricted =
			Session.getMaxFundingLimit().trustLevel === ACCOUNT_STATUS_RESTRICTED;

		if (isNewLineupPlan || hasCachedLimit || isAccountRestricted) {
			this.setState({ isCheckingFundingLimit: true });

			Session.loadMaxFundingLimit().then(({ maxFundingLimit }) => {
				this.setState({
					isCheckingFundingLimit: false,
					maxFundingLimit,
					isFundingLimitReached:
						maxFundingLimit !== null && maxFundingLimit <= controlTotal,
				});
			});
		}

		this.setState({
			isNsaSurchargeEnrolled,
			maxFundingLimit,
			nsaSurchargeRate: nsaSurchargePercentage,
			isInitialFundsPurchase: controlTotal === 0,
		});

		loadAdyenDataCollectionScript(getAdyenEnvironment());
	}

	componentWillUnmount() {
		if (this.customAmountDebounceTimer) {
			clearTimeout(this.customAmountDebounceTimer);
		}
	}

	async componentDidUpdate(_, prevState) {
		const { purchaseAmount, isNsaSurchargeEnrolled } = this.state;

		if (
			purchaseAmount > 0 &&
			prevState.purchaseAmount !== purchaseAmount &&
			isNsaSurchargeEnrolled
		) {
			this.setState({ isLoadingPreview: true });

			const { data } = await Communication.previewAddFunds({
				PurchaseAmount: purchaseAmount,
			});

			this.setState({
				previewCalculations: {
					totalAmount: data?.Amount?.Amount,
					purchaseAmount: data?.BaseAmount?.Amount,
					surchargeAmount: data?.TotalTransactionFee?.Amount,
				},
				isLoadingPreview: false,
			});
		}
	}

	/**
	 * Call purchasePostage endpoint
	 */
	purchasePostage = async () => {
		const {
			data,
			modalId,
			modalContext: { closeModal, showError, showMessage, showModal },
		} = this.props;
		const { onPurchaseSuccessful } = data || {};
		const { purchaseAmount, maxFundingLimit } = this.state;

		// show the `ProcessingPurchaseModal` dialog
		const processingPurchaseModalId = showModal({
			component: ProcessingPurchaseModal,
			closeCallback: () => {
				// close "AddFundsModal" component after closing the `ProcessingPurchaseModal` modal
				closeModal(modalId, ModalButtonActionType.Ok);
			},
		});

		let clientData;
		try {
			clientData = await getAdyenClientData();
		} catch {
			clientData = undefined;
		}

		const response = await Communication.purchasePostage({
			amount: purchaseAmount,
			clientData,
		});

		if (Communication.isSuccessful(response)) {
			// calls the callback passed by the caller
			if (onPurchaseSuccessful) {
				onPurchaseSuccessful();
			}

			Analytics.trackEvent(
				'addFunds',
				{
					amount: purchaseAmount,
					success: true,
				},
				Analytics.agent.segment
			);

			if (maxFundingLimit) {
				const balance = Session.getBalance();
				const { controlTotal = 0 } = balance;
				const remainingFundingLimit =
					maxFundingLimit - controlTotal - purchaseAmount;
				const status = remainingFundingLimit > 0 ? 'approaching' : 'reached';

				// Track the funding limit status after successful purchase
				Analytics.trackEvent(
					'funding-limit-purchase',
					{
						limit_remaining: remainingFundingLimit,
						purchase_amount: purchaseAmount,
						funding_status: status,
						blocked_status: 'not blocked',
						purchase_success: true,
					},
					Analytics.agent.segment
				);

				showModal({
					component: TransactionCompleteModal,
					data: {
						purchaseAmount,
						remainingFundingLimit,
					},
				});
			} else {
				const description = ReactDOMServer.renderToStaticMarkup(
					<div>
						<p>
							Your fund request for {usCurrencyFormatter.format(purchaseAmount)}{' '}
							has been approved.
						</p>
						{purchaseAmount === maxFundingLimit &&
							this.getInfoBanner('warning', maxFundingLimit)}
					</div>
				);

				showMessage({
					data: {
						title: 'Transaction Complete',
						description,
						primaryButtonType: ModalButtonActionType.Ok,
						primaryButtonText: 'OK',
					},
				});
			}
		} else {
			Analytics.trackEvent(
				'addFunds',
				{
					amount: purchaseAmount,
					success: false,
					error: response,
				},
				Analytics.agent.segment
			);

			if (maxFundingLimit) {
				const balance = Session.getBalance();
				const { controlTotal = 0 } = balance;
				const remainingFundingLimit = maxFundingLimit - controlTotal;
				const status = remainingFundingLimit > 0 ? 'approaching' : 'reached';

				// Track the funding limit status after unsuccessful purchase
				Analytics.trackEvent(
					'funding-limit-purchase',
					{
						limit_remaining: remainingFundingLimit,
						purchase_amount: purchaseAmount,
						funding_status: status,
						blocked_status: 'not blocked',
						purchase_success: false,
					},
					Analytics.agent.segment
				);
			}

			const error = {
				errorCode: response?.error?.errorCode,
				errorMessage: isString(response?.error?.errorMessage)
					? response.error.errorMessage
					: response.ErrorDescription,
			};
			const contactNumber = Utils.Util.getFraudNumber();

			if (error.errorCode === 6226727) {
				showError({
					data: {
						title: 'Payment Processing Error',
						description: `
							<p>We’ve encountered an error processing your payment. Please verify your payment information is correct and try again.</p>
							<p>If you continue to receive this message, please contact our support team for assistance at ${contactNumber} Monday–Friday 8am - 8pm CT.</p>
							`,
						primaryButtonText: 'OK',
						handleType: 'custom',
					},
				});
			} else {
				showError({ response });
			}
		}

		// close `ProcessingPurchaseModal` when the response comes back
		closeModal(processingPurchaseModalId);
	};

	/**
	 * Change selected add-fund option
	 * @param {object} event
	 */
	onRadioOptionChange = ({ target }) => {
		const { name, value } = target;

		this.setState({
			purchaseAmount: value,
			selectedOption: name,
			showInlineErrorMessage: false,
		});
	};

	/**
	 * Change the custom amount and selected add-fund option to `customAmount`
	 * @param {object} event
	 */
	onCustomAmountChange = ({ target }) => {
		const { name, value } = target;

		if (this.customAmountDebounceTimer) {
			clearTimeout(this.customAmountDebounceTimer);
		}

		this.customAmountDebounceTimer = setTimeout(() => {
			this.setState({
				selectedOption: name,
				purchaseAmount: value,
				customAmount: value,
				showInlineErrorMessage: false,
			});
		}, 500);
	};

	/**
	 * Show AutoFundAccountModal modal
	 */
	onAutoBuyPostageLinkClick = () => {
		const {
			modalContext: { showModal },
		} = this.props;

		showModal({
			component: AutoFundAccountModal,
		});
	};

	/**
	 * Navigate to editPaymentMethod
	 */
	onEditPaymentMethodLinkClick = () => {
		Navigation.navigateTo('editPaymentMethod');
	};

	/**
	 * Change `selectedOption` and `purchaseAmount` to `customAmount`
	 */
	onFocus = () => {
		const { customAmount } = this.state;

		this.setState({
			selectedOption: 'customAmount',
			purchaseAmount: customAmount,
			showInlineErrorMessage: false,
		});
	};

	/**
	 * Navigate to earnCredit
	 */
	onReferFriendLinkClick = () => {
		Navigation.navigateTo('earnCredit');
	};

	/**
	 * Validate `purchaseAmount` value before showing `ConfirmTransactionModal`
	 * Calculates total charge including surcharge for the actual transaction
	 */
	onPurchaseClick = async () => {
		const {
			modalContext: { showModal, showError },
		} = this.props;
		const { purchaseAmount, maxFundingLimit } = this.state;
		const balance = Session.getBalance();
		const { controlTotal = 0 } = balance;
		const remainingFundingLimit = maxFundingLimit - controlTotal;

		const errorMessage = {
			title: 'Notice',
			primaryButtonText: 'OK',
			handleType: 'custom',
		};

		if (
			maxFundingLimit &&
			purchaseAmount &&
			purchaseAmount > maxFundingLimit - controlTotal
		) {
			this.setState({
				showInlineErrorMessage: true,
			});

			// Track that user attempted to purchase postage after reaching their funding limit
			Analytics.trackEvent(
				'funding-limit-purchase',
				{
					limit_remaining: remainingFundingLimit,
					purchase_amount: purchaseAmount,
					funding_status: 'blocked',
					blocked_status: 'blocked',
					purchase_success: null,
				},
				Analytics.agent.segment
			);

			Logger.logToServer({
				limit_remaining: remainingFundingLimit,
				purchase_amount: purchaseAmount,
				funding_status: 'blocked',
				purchase_success: null,
			});
		} else {
			if (!purchaseAmount || purchaseAmount === 0) {
				return showError({
					data: {
						...errorMessage,
						description: '<p>Please select an amount to add.</p>',
					},
				});
			} else if (purchaseAmount < 10) {
				return showError({
					data: {
						...errorMessage,
						description: '<p>You must add at least $10.</p>',
					},
				});
			} else if (
				purchaseAmount + balance.amountAvailable >
				balance.maxBalanceAmountAllowed
			) {
				return showModal({
					component: AccountBalanceLimitModal,
					data: {
						maxPostageBalance: usCurrencyFormatter.format(
							balance.maxBalanceAmountAllowed
						),
					},
				});
			}

			showModal({
				component: ConfirmTransactionModal,
				data: {
					purchaseAmount: usCurrencyFormatter.format(purchaseAmount),
					purchasePostageCallback: this.purchasePostage,
				},
				closeCallback: type => {
					/**
					 * When click the "Confirm" button, continue the purchase flow
					 * "Confirm" type is used to differentiate from "Esc" or "X" (which are "OK" type)
					 */
					if (type === ModalButtonActionType.Confirm) {
						this.purchasePostage();
					}
				},
			});
		}
	};

	getInfoBanner = (infoBannerType, lifetimeFundingLimit) => {
		const balance = Session.getBalance();
		const { controlTotal = 0 } = balance;
		const remainingFundingLimit = lifetimeFundingLimit - controlTotal;

		Logger.logToServer({
			funding_limit_reached: true,
			maxFundingLimit: lifetimeFundingLimit,
			funding_status: 'reached',
		});

		return (
			<InlineMessage
				type={infoBannerType}
				header={`${usCurrencyFormatter.format(remainingFundingLimit)} of your funding limit remains.`}
				text={
					'To remove this limit, you can verify your identity using our verification tool or chat with a live agent (available Monday–Friday 8am - 8pm CT).'
				}
				actions={
					<div className="action-buttons">
						<WrappedButton
							label="Verify now"
							buttonType={WrappedButtonType.TertiaryWhite}
							dataqa="start-identity-verification"
							onClick={() => {
								const {
									modalId,
									modalContext: { closeModal },
									startVerification,
								} = this.props;
								closeModal(modalId, ModalButtonActionType.Ok);

								const { controlTotal = 0 } = Session.getBalance();
								const { maxFundingLimit } = Session.getMaxFundingLimit();

								const trackVerificationOutcome = outcome => {
									Analytics.trackEvent(
										'plaid-verification-attempt',
										{
											limit_remaining: maxFundingLimit - controlTotal,
											outcome,
										},
										Analytics.agent.segment
									);
								};

								startVerification({
									onSuccess: () => trackVerificationOutcome('success'),
									onFailure: () => trackVerificationOutcome('failure'),
									onCancel: () => trackVerificationOutcome('aborted'),
								});
							}}
						/>
						<WrappedButton
							label="Start chat"
							buttonType={WrappedButtonType.TertiaryWhite}
							dataqa="start-chat"
							onClick={() => {
								const {
									modalId,
									modalContext: { closeModal, showModal },
								} = this.props;
								closeModal(modalId, ModalButtonActionType.Ok);

								Analytics.trackEvent(
									'live-chat-transfer',
									{
										limit_remaining: remainingFundingLimit,
									},
									Analytics.agent.segment
								);

								launchChatbot({
									flags: this.props.flags || {},
									showModal,
									trigger: ChatbotTriggers.AddFunds,
								});
							}}
						/>
					</div>
				}
				dataqa="warning-message"
			/>
		);
	};

	render() {
		const { modalId } = this.props;
		const {
			customAmount,
			selectedOption,
			showInlineErrorMessage,
			isNsaSurchargeEnrolled,
			nsaSurchargeRate,
			previewCalculations,
			isLoadingPreview,
			isInitialFundsPurchase,
			isFundingLimitReached,
			maxFundingLimit,
			isCheckingFundingLimit,
		} = this.state;
		const balance = Session.getBalance();
		const { trustLevel } = Session.getMaxFundingLimit();
		const { controlTotal = 0 } = balance;
		let infoBannerType = null;

		if (maxFundingLimit) {
			if (showInlineErrorMessage) {
				infoBannerType = 'error';
			} else if (controlTotal < maxFundingLimit) {
				infoBannerType = 'information';
			} else {
				infoBannerType = 'warning';
			}
		}

		const isAccountRestricted = trustLevel === ACCOUNT_STATUS_RESTRICTED;

		const shouldShowTotalChargeSummary =
			isNsaSurchargeEnrolled && nsaSurchargeRate > 0;
		const shouldShowInfoBanner =
			infoBannerType === 'error' || (!isInitialFundsPurchase && infoBannerType);

		if (isCheckingFundingLimit) {
			return <Spinner msg="Checking funding limit..." />;
		}

		if (isAccountRestricted) {
			return <FundingRestrictedModal modalId={modalId} />;
		}

		if (isFundingLimitReached) {
			return <FundingLimitReachedModal modalId={modalId} />;
		}

		return (
			<Modal
				modalId={modalId}
				className="common-payment add-funds-modal"
				header="Add Funds"
				primaryButton={{
					text: 'Purchase',
					type: ModalButtonActionType.Ok,
					onClick: this.onPurchaseClick,
				}}
				size="small"
			>
				{shouldShowInfoBanner &&
					this.getInfoBanner(infoBannerType, maxFundingLimit)}
				<div className="add-funds-radio-buttons">
					{/* Default add fund options */}
					{[10, 25, 50, 100].map(value => (
						<WrappedRadioButton
							key={`input-option-${value}`}
							inputId={`input-option-${value}`}
							name={value}
							value={value}
							onChange={this.onRadioOptionChange}
							checked={value === selectedOption}
							dataqa={`add-funds-${value}`}
						>
							{usCurrencyFormatter.format(value)}
						</WrappedRadioButton>
					))}
					<div className="add-other-amount">
						<WrappedRadioButton
							name="customAmount"
							inputId="add-funds-custom-amount"
							onChange={this.onRadioOptionChange}
							checked={selectedOption === 'customAmount'}
							dataqa="add-funds-custom-amount"
						/>
						<WrappedInputNumberWithLabel
							className="custom-amount-input"
							label="Other: "
							uiElementConfig={{
								dataqa: 'purchase-other-amount',
								prefix: '$ ',
								placeholder: '$ 0.00',
								name: 'customAmount',
								value: customAmount,
								min: 0,
								mode: 'decimal',
								minFractionDigits: 2,
								maxFractionDigits: 2,
								incrementButtonIcon: 'pi pi-chevron-up',
								decrementButtonIcon: 'pi pi-chevron-down',
								showButtons: true,
								onChange: this.onCustomAmountChange,
								onFocus: this.onFocus,
							}}
						/>
					</div>
				</div>
				<div className="action-buttons">
					<WrappedButton
						label="Edit payment method"
						className="link"
						buttonType={WrappedButtonType.TertiaryGrey}
						onClick={this.onEditPaymentMethodLinkClick}
						dataqa="edit-payment-method"
					/>
					<WrappedButton
						label="Auto-fund account"
						className="link"
						buttonType={WrappedButtonType.TertiaryGrey}
						onClick={this.onAutoBuyPostageLinkClick}
						dataqa="auto-fund-account"
					/>
				</div>

				<Link
					className="link-with-icon"
					data-qa="earn-free-account-credit"
					dataqa="add-funds-earn-free-account-credit-link"
					onClick={this.onReferFriendLinkClick}
					navIcon={true}
				>
					Earn Free Account Credit
				</Link>

				{shouldShowTotalChargeSummary && (
					<TotalChargeSummary
						className="total-charge-summary-container"
						purchaseAmount={previewCalculations?.purchaseAmount}
						surchargeAmount={previewCalculations?.surchargeAmount}
						totalAmount={previewCalculations?.totalAmount}
						isLoading={isLoadingPreview}
					/>
				)}
			</Modal>
		);
	}
}

export default withLDConsumer()(
	withLayoutAndModalContexts(withIdentityVerification(AddFundsModal))
);
